import { Component, inject, ChangeDetectionStrategy, forwardRef } from '@angular/core';

import { ReactiveFormsModule, FormGroup, Validators } from '@angular/forms';
import {
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  CreatePlanRequest,
  JobSource,
} from '../../shared/interfaces/execution-plan.interfaces';
import {
  BaseRoutineDialogComponent,
  BaseRoutineDialogData,
} from '../../shared/base-routine-dialog/base-routine-dialog.component';
import { CodeEditorComponent } from '../../shared/modules/code-editor/code-editor.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export interface AddPlanDialogData extends BaseRoutineDialogData {}

@Component({
  selector: 'app-add-plan-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCheckboxModule,
    // forwardRef breaks an NG0919 circular import: opened via FeatureDialogService
    // (imported by DialogService), and CodeEditorComponent injects DialogService
    // (SCRUM-718, follow-up to SCRUM-689).
    forwardRef(() => CodeEditorComponent),
  ],
  templateUrl: './add-plan-dialog.component.html',
  styleUrls: ['./add-plan-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddPlanDialogComponent extends BaseRoutineDialogComponent {
  private dialogRef = inject(MatDialogRef<AddPlanDialogComponent>);

  // Injected data
  public data = inject<AddPlanDialogData>(MAT_DIALOG_DATA);

  protected override addCustomFormControls(form: FormGroup): void {
    form.get('workflowId')?.setValidators([Validators.required]);
    form.addControl('versionId', this.fb.control(null, [Validators.required]));
  }

  protected override onInitHook(): void {
    // When useHead changes, update versionId validators and load appropriate workflows
    this.routineForm
      .get('useHead')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((useHead: boolean) => {
        this.updateVersionIdValidation(useHead);
        const targetVersionId = useHead
          ? this.getHeadVersionId()
          : (this.routineForm.get('versionId')?.value ?? this.getLatestVersionId());
        if (targetVersionId != null) {
          this.loadProjectWorkflows(targetVersionId);
        }
      });

    // When versionId changes (and useHead is false), reload workflows for the selected version
    this.routineForm
      .get('versionId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((versionId: number | null) => {
        if (!this.routineForm.get('useHead')?.value && versionId != null) {
          this.loadProjectWorkflows(versionId);
        }
      });
  }

  private updateVersionIdValidation(useHead: boolean): void {
    const versionControl = this.routineForm.get('versionId');
    if (!versionControl) return;

    if (useHead) {
      versionControl.clearValidators();
      versionControl.setValue(null);
    } else {
      versionControl.setValidators([Validators.required]);
      // Restore default version selection when unchecking useHead
      const selectedVersionId = this.getLatestVersionId();
      if (selectedVersionId != null) {
        versionControl.setValue(selectedVersionId);
      }
      if (this.hasOnlyOneVersion) {
        versionControl.disable();
      } else {
        versionControl.enable();
      }
    }
    versionControl.updateValueAndValidity();
  }

  protected override onVersionsProcessed(): void {
    if (this.projectVersions.length === 0) {
      // No published versions — automatically enable HEAD version execution.
      // Also clear versionId validators directly here because the valueChanges
      // subscription in onInitHook() may not be set up yet when project data
      // resolves synchronously (e.g. in tests or cached responses).
      this.routineForm.get('useHead')?.setValue(true);
      this.updateVersionIdValidation(true);
      const headId = this.getHeadVersionId();
      if (headId != null) {
        this.loadProjectWorkflows(headId);
      }
      return;
    }

    // Set default version (latest published version) only when not using HEAD
    if (!this.routineForm.get('useHead')?.value) {
      const selectedVersionId = this.getLatestVersionId();
      if (selectedVersionId != null) {
        this.setFormVersionId(selectedVersionId);
        this.loadProjectWorkflows(selectedVersionId);
      }
      if (this.hasOnlyOneVersion) {
        this.routineForm.get('versionId')?.disable();
      } else {
        this.routineForm.get('versionId')?.enable();
      }
    } else {
      const headId = this.getHeadVersionId();
      if (headId != null) {
        this.loadProjectWorkflows(headId);
      }
    }
  }

  protected override onProjectDataLoaded(): void {
    // Final check to ensure version is selected (unless useHead is active)
    if (!this.routineForm.get('useHead')?.value) {
      this.ensureDefaultVersionSelected();
    }
  }

  protected override getFieldLabels(): { [key: string]: string } {
    return {
      name: 'Routine Name',
      description: 'Description',
      runsAsId: 'Runs as User',
      workflowId: 'Workflow',
      versionId: 'Version',
      crontab: 'Schedule',
      arguments: 'Arguments (JSON)',
    };
  }

  /**
   * Set version ID in form with proper validation
   */
  private setFormVersionId(versionId: number): void {
    const versionControl = this.routineForm.get('versionId');
    if (versionControl) {
      // emitEvent:false so this programmatic default-set does not trigger the
      // versionId valueChanges subscriber; callers issue the workflow load
      // explicitly, and emitting here caused a duplicate GET on dialog open.
      versionControl.setValue(versionId, { emitEvent: false });
      versionControl.markAsTouched();
      versionControl.updateValueAndValidity();
    }
  }

  /**
   * Manually set the default version if not already set
   */
  private ensureDefaultVersionSelected(): void {
    const currentVersionId = this.routineForm.get('versionId')?.value;

    if (currentVersionId == null) {
      const selectedVersionId = this.getLatestVersionId();
      if (selectedVersionId != null) {
        this.setFormVersionId(selectedVersionId);
        this.loadProjectWorkflows(selectedVersionId);
      }
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.validateForm()) {
      return;
    }

    // Backend requires a workflow for HEAD routines (workflow_id or workflow_name).
    // Validate this before sending the request to surface a clear error to the user.
    const formValue = this.routineForm.value;
    if (formValue.useHead && !formValue.workflowId) {
      this.error.set('A workflow must be selected when "Execute against HEAD version" is enabled.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    try {
      const planRequest = this.createPlanRequestFromForm(
        this.routineForm.value,
      );

      this.executionPlanService
        .createPlan(this.data.projectId, planRequest)
        .subscribe({
          next: (result: any) => {
            this.loading.set(false);
            this.dialogRef.close(result);
          },
          error: (error: any) => {
            this.loading.set(false);
            this.error.set(this.ErrorHandlingUtils.getErrorMessage(error));
          },
        });
    } catch (error: any) {
      this.loading.set(false);
      this.error.set(error.message || 'Failed to create routine request');
    }
  }

  private createPlanRequestFromForm(formValue: any): CreatePlanRequest {
    const isWebhook = formValue.source === 'webhook';
    // A crontab takes precedence: if the user picked a schedule, the routine is
    // Scheduled regardless of what the source dropdown says (the create dialog
    // only shows a cron picker, not a separate source selector).
    const hasCrontab = !!(formValue.crontab?.trim());
    const source: JobSource = hasCrontab
      ? JobSource.Scheduled
      : isWebhook
        ? JobSource.Webhook
        : JobSource.Manual;

    const request: CreatePlanRequest = {
      name: formValue.name,
      description: formValue.description || '',
      runsAsId: formValue.runsAsId,
      workflowId: formValue.workflowId || undefined,
      source,
    };

    // Published version binding — server expects versionId when not using HEAD (matches plans page + SCRUM-382).
    if (
      !formValue.useHead &&
      formValue.versionId != null &&
      formValue.versionId !== ''
    ) {
      request.versionId = Number(formValue.versionId);
    }

    // Include crontab for scheduled routines
    if (hasCrontab) {
      request.crontab = formValue.crontab.trim();
    }

    // Add webhook fields for webhook routines
    if (isWebhook) {
      request.webhookType = formValue.webhookType;
      request.webhookSecretId1 = formValue.webhookSecretId1;
      request.webhookSecretId2 = formValue.webhookSecretId2;
    }

    // Add HEAD version flag for manual routines
    if (formValue.useHead) {
      request.useHead = true;
    }

    // Routine-scoped job arguments (only when provided)
    const argumentsValue = (formValue.arguments ?? '').trim();
    if (argumentsValue) {
      request.arguments = argumentsValue;
    }

    return request;
  }
}
